/**
 * /admin/ops — unified admin "Operations" command center.
 *
 * Combines the former System Health panel (GET /v1/admin/health) and the Live
 * Ops voice monitor (GET /v1/admin/voice-ops) into one screen with three
 * stacked sections:
 *
 *   1. System health — a strip of compact status tiles, one per subsystem
 *      component, each with a colored left bar + dot, a headline metric, and a
 *      one-line detail. An overall-status pill in the header shows the worst.
 *   2. Live voice throughput — a KPI band aggregated across every running
 *      campaign (running count, ~active calls, aggregate CPS, answered today,
 *      transfers today, machine %).
 *   3. Live campaigns — one row per RUNNING voice campaign with an
 *      actual/target CPS column, the recent 503 signal, live pacing /
 *      concurrency controls, the concurrency-cap / 603 badges, a per-row
 *      semaphore, and the icon-only operator actions. Paused campaigns are
 *      NOT in this table: they live behind the header's "Paused (n)" button,
 *      which opens a panel to consult and reactivate them. The single
 *      exception is a campaign mid-restart — technically paused for ~35s, it
 *      stays in the table labelled "restarting…" so the operator watching the
 *      toggle never loses sight of it.
 *
 * Sections 1-2 are read-only. Section 3 carries the OPERATOR CONTROLS added
 * after the 2026-09-03 incident (campaign 54 sat at 0 CPS with status=running
 * because the poller's runner died and was never respawned; the fix was a
 * status toggle applied by hand with raw SQL against prod):
 *
 *   - Pause / Activate - plain status transitions, driven through the campaign
 *     service's transition graph.
 *   - Restart runner - the toggle. Pauses now, resumes ~35s later so the
 *     voicedispatcher poller gets a full scan cycle to drop its stale runner
 *     entry. The row shows "restarting..." for that window.
 *   - Pacing (CPS) + concurrency - applied LIVE by the running runner; no
 *     restart, no dropped calls. The trailing-5min 503 rate sits right next to
 *     them so the effect of a change is visible while tuning.
 *
 * Pause / Activate / Restart render as ICON-ONLY buttons (IconAction): the
 * table is already wider than the viewport, and the action names survive as
 * the native tooltip + aria-label, with a legend in the table footer.
 *
 * Every state-changing action goes through ConfirmModal (never
 * window.confirm), and every one is platform-admin only - the API enforces
 * tenant_type=platform on the write endpoints on top of the admin role guard,
 * so a reseller admin sees the table but gets a 403 from the buttons.
 *
 * Each section renders its own loading/empty/error independently (health may
 * resolve before voice-ops). Colors are semantic and token-only.
 */

import { type JSX, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Icon } from '@/components/icons';
import { CpsValue } from '@/components/ops/CpsValue';
import { pct0, pct1 } from '@/components/ops/format';
import { type KpiItem, KpiGrid } from '@/components/ops/KpiTile';
import { ActionLegend, Legend } from '@/components/ops/Legend';
import { LiveToggle, RefreshButton, UpdatedAgo } from '@/components/ops/LiveControls';
import { SectionLabel } from '@/components/ops/SectionLabel';
import { type OpsSem as Sem, semBar, semDot, semTag, semText } from '@/components/ops/sem';
import { Button, Card, ConfirmModal, IconAction, Input, WideModal } from '@/components/ui';
import { useAdminHealth, type HealthComponent, type HealthReport, type HealthStatus } from '@/hooks/useAdminHealth';
import {
  RECENT_FAIL_MIN_DIALS,
  TRANSFERS_TODAY_MAX,
  TRANSFERS_TODAY_PAGE,
  useAdminVoiceOps,
  useAdminVoiceOpsTransfersToday,
  useCampaignOpsActions,
  type VoiceOpsCampaign,
  type VoiceOpsTransfer,
  type VoiceOpsTransfersPage,
  type VoiceOpsTransferState,
} from '@/hooks/useAdminVoiceOps';
import { formatCurrency, formatDateTime, formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// Map an API health status to a semantic color bucket. "unknown" is muted
// (grey) so it reads as "no signal", never as a failure — matching the backend
// rule that unknown never escalates the overall status.
const statusSem: Record<HealthStatus, Sem> = {
  ok: 'success',
  warn: 'warn',
  crit: 'danger',
  unknown: 'muted',
};

// --- Campaign-row thresholds (ported from the two source pages) -------------
// 603 = SIP "Decline" — the carrier rejected the call. A rising share is the
// classic block/spam-label signal, so it gets the sharpest thresholds.
const C603_WARN = 0.05;
const C603_DANGER = 0.2;
// Balance floor below which a running campaign is one dial from stalling.
const BALANCE_LOW = 10;
// CPS reads "far below target" (amber) when running under half the configured
// per-second rate — alongside the explicit concurrency-cap flag.
const CPS_LOW_RATIO = 0.5;
// Trailing-5min SIP-503 share. 503 = the carrier refusing the offered rate, so
// this is the pacing dial's feedback signal, not a call-quality metric. The
// thresholds come from the 2026-09-03 measurements: ~15% at a pacing the trunk
// accepted (360/min), ~76% at one it did not (900/min). Amber lands well before
// red so an operator throttles back before the carrier sheds traffic in bulk.
const FAIL503_WARN = 0.15;
const FAIL503_DANGER = 0.4;
// How long a "restart runner" parks the campaign in paused before the API
// resumes it. Mirrors restartResumeDelay in
// api/internal/services/admin_campaign_ops.go, which must stay above the
// voicedispatcher's 30s poll interval — the poller has to OBSERVE the
// not-running state to drop its stale runner entry. Display only: the real
// countdown is server-side and each response carries resume_in_seconds.
const RESTART_SECONDS = 35;
// How far back the API looks for paused campaigns. Mirrors pausedWindow in
// api/internal/services/admin_voice_ops.go — display only, so the paused
// panel can say exactly what it is (and is not) showing.
const PAUSED_WINDOW_DAYS = 7;

// isLiveRow decides which of the two sets a payload row belongs to. The
// endpoint returns running campaigns AND recently-paused ones in one list;
// the main table shows only what is actually dialing, PLUS any campaign a
// restart toggle is holding in 'paused' right now — dropping that row
// mid-restart is exactly the regression the "restarting…" state exists to
// prevent. Everything else is a paused row for the paused panel.
function isLiveRow(c: VoiceOpsCampaign): boolean {
  return c.status === 'running' || c.restarting;
}

function c603Pct(c: VoiceOpsCampaign): number {
  return c.total > 0 ? c.c603 / c.total : 0;
}

function machinePct(c: VoiceOpsCampaign): number {
  // Machine share of answered calls (bill_seconds>0 → answered).
  return c.answered > 0 ? c.machine / c.answered : 0;
}

function c603Sem(c: VoiceOpsCampaign): Sem {
  const p = c603Pct(c);
  if (p > C603_DANGER) return 'danger';
  if (p > C603_WARN) return 'warn';
  return 'muted';
}

// hasFailSignal gates the failure percentages on a big enough denominator —
// "100%" computed from two dials is noise, and retuning pacing off it is worse
// than showing a dash.
function hasFailSignal(c: VoiceOpsCampaign): boolean {
  return c.recent_dials >= RECENT_FAIL_MIN_DIALS;
}

function fail503Sem(c: VoiceOpsCampaign): Sem {
  if (!hasFailSignal(c)) return 'muted';
  if (c.recent_503_pct > FAIL503_DANGER) return 'danger';
  if (c.recent_503_pct > FAIL503_WARN) return 'warn';
  return 'success';
}

function balanceSem(c: VoiceOpsCampaign): Sem {
  if (c.available <= 0) return 'danger';
  if (c.available < BALANCE_LOW) return 'warn';
  return 'muted';
}

// Pileup: more leads parked in-call than the approximate active count explains.
function isPileup(c: VoiceOpsCampaign): boolean {
  return c.incall_leads > c.active_approx && c.incall_leads > 0;
}

// cpsAmber: the CPS cell turns amber when the campaign is concurrency-capped or
// dialing well under its configured target.
function cpsAmber(c: VoiceOpsCampaign): boolean {
  if (c.status !== 'running') return false;
  if (c.concurrency_capped) return true;
  return c.cps_target > 0 && c.cps_current < c.cps_target * CPS_LOW_RATIO;
}

// A campaign carries a red flag when any danger-level condition holds: empty
// balance, a 603 storm, a 503 storm, or a stall (running but no dials in the
// last 60s). A PAUSED campaign is exempt from the stall + 503 checks: it is not
// dialing on purpose, so 0 CPS is the expected state there, not a fault.
function hasRedFlag(c: VoiceOpsCampaign): boolean {
  if (c.available <= 0 || c603Pct(c) > C603_DANGER) return true;
  if (c.status !== 'running') return false;
  return c.cps_current === 0 || (hasFailSignal(c) && c.recent_503_pct > FAIL503_DANGER);
}

// Per-row semaphore: red for any danger condition, amber for any attention
// condition (cap, elevated 603, low balance, pileup, low CPS), else green.
function rowSem(c: VoiceOpsCampaign): Sem {
  if (hasRedFlag(c)) return 'danger';
  // A restart in flight is a deliberate, transient state — amber, never red.
  if (c.restarting) return 'warn';
  if (
    c.concurrency_capped ||
    c603Pct(c) > C603_WARN ||
    (c.available > 0 && c.available < BALANCE_LOW) ||
    (c.status === 'running' && (isPileup(c) || cpsAmber(c) || fail503Sem(c) === 'warn'))
  ) {
    return 'warn';
  }
  return 'success';
}

/**
 * A state-changing action awaiting confirmation. Held at page level because
 * ConfirmModal is rendered once at the root (its documented usage) rather than
 * once per row.
 */
type PendingAction =
  | { kind: 'pause' | 'resume' | 'restart'; c: VoiceOpsCampaign }
  | { kind: 'pacing'; c: VoiceOpsCampaign; pacingCpm: number; maxConcurrent: number };

interface KpiAgg {
  running: number;
  cps: number;
  active: number;
  answered: number;
  transfers: number;
  machinePct: number;
  hasAnswered: boolean;
}

export default function AdminOpsPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const [live, setLive] = useState(true);
  const health = useAdminHealth({ live });
  const voice = useAdminVoiceOps({ live });
  const actions = useCampaignOpsActions();
  const rows = useMemo(() => voice.data ?? [], [voice.data]);
  // The live table and the paused panel are two views of the SAME 10s poll.
  const liveRows = useMemo(() => rows.filter(isLiveRow), [rows]);
  const pausedRows = useMemo(() => rows.filter((c) => !isLiveRow(c)), [rows]);
  const [pausedOpen, setPausedOpen] = useState(false);
  // Drill-down behind the "Transfers today" KPI tile. Closed by default and
  // its query is disabled while closed, so the extra endpoint is only ever hit
  // on an explicit click.
  const [transfersOpen, setTransfersOpen] = useState(false);
  // The action controls only exist for a platform admin (the API rejects
  // everyone else with a 403), so the KPI band and the table stay identical for
  // a reseller — they just don't get the Actions column.
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const busyId =
    actions.pause.isPending || actions.resume.isPending || actions.restart.isPending || actions.updatePacing.isPending
      ? (pending?.c.campaign_id ?? null)
      : null;
  const components = useMemo(() => health.data?.components ?? [], [health.data]);
  const updatedAt = Math.max(health.dataUpdatedAt, voice.dataUpdatedAt);
  const fetching = health.isFetching || voice.isFetching;

  const kpi: KpiAgg = useMemo(() => {
    let cps = 0;
    let active = 0;
    let answered = 0;
    let transfers = 0;
    let machine = 0;
    let running = 0;
    for (const c of rows) {
      // Aggregated over the WHOLE payload, paused rows included: "answered /
      // transfers today" is the day's work, and a campaign stopped at noon
      // still did it. Only the "Running" counter is state-sensitive, and the
      // live columns (cps, active) are ~0 for a stopped campaign anyway.
      if (c.status === 'running') running += 1;
      cps += c.cps_current;
      active += c.active_approx;
      answered += c.answered;
      transfers += c.transfers;
      machine += c.machine;
    }
    return {
      running,
      cps: Math.round(cps * 10) / 10,
      active,
      answered,
      transfers,
      machinePct: answered > 0 ? machine / answered : 0,
      hasAnswered: answered > 0,
    };
  }, [rows]);

  const refreshAll = (): void => {
    void health.refetch();
    void voice.refetch();
  };

  // Open the confirmation for an action; clear any error left from a previous
  // attempt so the modal doesn't open pre-filled with a stale failure.
  const askConfirm = (a: PendingAction): void => {
    setActionError(null);
    setPending(a);
  };

  // Run the confirmed action. On failure the modal STAYS OPEN with the error —
  // ConfirmModal deliberately doesn't self-close, so the operator sees why
  // nothing happened instead of the dialog vanishing silently.
  const confirmPending = async (): Promise<void> => {
    if (!pending) return;
    setActionError(null);
    try {
      switch (pending.kind) {
        case 'pause':
          await actions.pause.mutateAsync(pending.c.campaign_id);
          break;
        case 'resume':
          await actions.resume.mutateAsync(pending.c.campaign_id);
          break;
        case 'restart':
          await actions.restart.mutateAsync(pending.c.campaign_id);
          break;
        case 'pacing':
          await actions.updatePacing.mutateAsync({
            campaignId: pending.c.campaign_id,
            pacingCpm: pending.pacingCpm,
            maxConcurrent: pending.maxConcurrent,
          });
          break;
      }
      setPending(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('ops_action_failed'));
    }
  };

  return (
    <div className="px-6 py-6 max-w-[1400px] mx-auto">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="display text-[24px] font-semibold tracking-tight text-text leading-tight m-0 inline-flex items-center gap-2">
            <Icon.eye className="w-5 h-5 text-volt-500" aria-hidden />
            {t('ops_title')}
          </h1>
          <p className="text-[13px] text-text-4 mt-1">{t('ops_subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <OverallPill report={health.data} loading={health.isLoading} error={health.isError} />
          {voice.data && <PausedButton count={pausedRows.length} onClick={() => setPausedOpen(true)} />}
          <UpdatedAgo updatedAt={updatedAt} />
          <RefreshButton fetching={fetching} onClick={refreshAll} />
          <LiveToggle on={live} fetching={fetching} onToggle={() => setLive((v) => !v)} />
        </div>
      </header>

      {/* ---------------------------------------------------------------- */}
      {/* Section 1 — System health */}
      {/* ---------------------------------------------------------------- */}
      <SectionLabel>{t('ops_section_health')}</SectionLabel>
      {health.isError ? (
        <Card className="p-6 text-center text-[13px] text-text-3">{t('health_load_error')}</Card>
      ) : health.isLoading || !health.data ? (
        <Card className="p-8 text-center text-[13px] text-text-4">{t('health_loading')}</Card>
      ) : (
        // Tiles are rendered generically from whatever /v1/admin/health
        // returns, so a new component (and, for FreeSWITCH capacity, a new
        // voice node) appears here with no change to this file. The xl track
        // count fits the eight components a single-voice-node install reports;
        // extra nodes wrap onto a second row.
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-8 gap-2.5">
          {components.map((c) => (
            <HealthTile key={c.key} c={c} />
          ))}
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Section 2 — Live voice throughput */}
      {/* ---------------------------------------------------------------- */}
      <SectionLabel className="mt-8">{t('ops_section_throughput')}</SectionLabel>
      {voice.isError ? (
        <Card className="p-6 text-center text-[13px] text-text-3">{t('vops_load_error')}</Card>
      ) : voice.isLoading ? (
        <Card className="p-8 text-center text-[13px] text-text-4">{t('vops_loading')}</Card>
      ) : (
        <KpiBand kpi={kpi} lang={lang} onOpenTransfers={() => setTransfersOpen(true)} />
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Section 3 — Running campaigns */}
      {/* ---------------------------------------------------------------- */}
      <SectionLabel className="mt-8" aside={voice.data ? t('ops_campaigns_count', { count: kpi.running }) : undefined}>
        {t('ops_section_campaigns')}
      </SectionLabel>
      {voice.isError ? (
        <Card className="p-6 text-center text-[13px] text-text-3">{t('vops_load_error')}</Card>
      ) : voice.isLoading ? (
        <Card className="p-8 text-center text-[13px] text-text-4">{t('vops_loading')}</Card>
      ) : liveRows.length === 0 ? (
        <Card className="p-10 text-center text-[13px] text-text-4">{t('ops_campaigns_empty')}</Card>
      ) : (
        <Card padding="none" className="overflow-hidden">
          <div className="overflow-x-auto">
            <OpsTable rows={liveRows} lang={lang} busyId={busyId} onAction={askConfirm} />
          </div>
          <TableFooter />
        </Card>
      )}

      {/* Paused campaigns — consult + reactivate. Rendered BEFORE the
          ConfirmModal: both overlays are z-50, so DOM order decides which one
          paints on top, and the confirmation must win. */}
      {pausedOpen && (
        <PausedPanel
          rows={pausedRows}
          lang={lang}
          busyId={busyId}
          onAction={askConfirm}
          onClose={() => setPausedOpen(false)}
        />
      )}

      {/* Transfers-today drill-down. Same ordering rule as the paused panel:
          before the ConfirmModal so a confirmation always paints on top. */}
      {transfersOpen && <TransfersTodayPanel lang={lang} onClose={() => setTransfersOpen(false)} />}

      {/* Single ConfirmModal for every state-changing action on this page.
          window.confirm / alert are forbidden project-wide. */}
      <ActionConfirmModal
        pending={pending}
        busy={busyId !== null}
        error={actionError}
        onConfirm={() => void confirmPending()}
        onClose={() => {
          setPending(null);
          setActionError(null);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action confirmation
// ---------------------------------------------------------------------------

// ActionConfirmModal renders the right copy for whichever action is pending.
// Each description states plainly what will happen — in particular that a
// restart parks the campaign for ~35s, and that a pacing change needs no
// restart at all.
function ActionConfirmModal({
  pending,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  pending: PendingAction | null;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}): JSX.Element | null {
  const { t } = useTranslation('default');
  if (!pending) return null;

  const name = pending.c.campaign_name;
  const tenant = pending.c.tenant_name;

  let title = '';
  let body: ReactNode = null;
  let confirmLabel = '';
  let tone: 'danger' | 'primary' = 'primary';

  switch (pending.kind) {
    case 'pause':
      title = t('ops_confirm_pause_title');
      body = t('ops_confirm_pause_desc', { name, tenant });
      confirmLabel = t('ops_action_pause');
      tone = 'danger';
      break;
    case 'resume':
      title = t('ops_confirm_activate_title');
      body = t('ops_confirm_activate_desc', { name, tenant });
      confirmLabel = t('ops_action_activate');
      break;
    case 'restart':
      title = t('ops_confirm_restart_title');
      body = t('ops_confirm_restart_desc', { name, tenant, seconds: RESTART_SECONDS });
      confirmLabel = t('ops_action_restart');
      tone = 'danger';
      break;
    case 'pacing':
      title = t('ops_confirm_pacing_title');
      body = (
        <>
          <div>
            {t('ops_confirm_pacing_desc', {
              name,
              tenant,
              oldCpm: pending.c.pacing_cpm,
              newCpm: pending.pacingCpm,
              oldConc: pending.c.max_concurrent,
              newConc: pending.maxConcurrent,
            })}
          </div>
          <div className="mt-2 text-success text-[12.5px]">{t('ops_pacing_live_note')}</div>
        </>
      );
      confirmLabel = t('ops_action_apply');
      break;
  }

  return (
    <ConfirmModal
      open
      title={title}
      description={
        <>
          {body}
          {error && (
            <div className="mt-3 rounded-md bg-danger/10 text-danger px-3 py-2 text-[12.5px]" role="alert">
              {error}
            </div>
          )}
        </>
      }
      confirmLabel={confirmLabel}
      cancelLabel={t('ops_confirm_cancel')}
      tone={tone}
      busy={busy}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
}

// ---------------------------------------------------------------------------
// Header widgets
// ---------------------------------------------------------------------------

// OverallPill mirrors the mockup's header status pill: worst-of-health with a
// pulsing dot. Muted while health loads or errors.
function OverallPill({
  report,
  loading,
  error,
}: {
  report: HealthReport | undefined;
  loading: boolean;
  error: boolean;
}): JSX.Element {
  const { t } = useTranslation('default');
  if (error) {
    return (
      <span className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12.5px] font-semibold', semTag.muted)}>
        <span className={cn('inline-block w-2 h-2 rounded-full', semDot.muted)} aria-hidden />
        {t('ops_overall_unknown')}
      </span>
    );
  }
  if (loading || !report) {
    return (
      <span className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12.5px] font-semibold', semTag.muted)}>
        <span className={cn('inline-block w-2 h-2 rounded-full animate-pulse', semDot.muted)} aria-hidden />
        {t('ops_overall_loading')}
      </span>
    );
  }
  const sem = statusSem[report.overall];
  return (
    <span
      className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12.5px] font-semibold', semTag[sem])}
      role="status"
    >
      <span className={cn('inline-block w-2 h-2 rounded-full', semDot[sem], report.overall === 'ok' && 'animate-pulse')} aria-hidden />
      {t(`health_overall_${report.overall}`)}
    </span>
  );
}

// PausedButton — the header entry point to the paused panel. It carries the
// count so an operator sees at a glance that something is stopped without
// opening anything; disabled at zero rather than hidden, so the affordance
// stays in a stable place.
function PausedButton({ count, onClick }: { count: number; onClick: () => void }): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={count === 0}
      title={t('ops_paused_button_help', { days: PAUSED_WINDOW_DAYS })}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[12px] font-medium border transition-colors',
        'border-line text-text-3 hover:text-text hover:bg-surface-2',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-text-3',
      )}
    >
      <Icon.stop className="w-3.5 h-3.5" aria-hidden />
      {t('ops_paused_button', { count })}
    </button>
  );
}

// PausedPanel — everything paused within the API's window, in a WideModal
// (the shared primitive; no bespoke overlay). It is a DECISION surface, not a
// monitoring one: the live columns of a stopped campaign are all zero, so it
// shows what actually helps decide whether to start it again — who owns it,
// how it is paced, how long it has been stopped, how many leads are still
// waiting, what it did today, and whether the tenant can pay for more.
// Activate goes through the same askConfirm → ConfirmModal → mutation path as
// the table, so there is exactly one way to change a campaign's state here.
function PausedPanel({
  rows,
  lang,
  busyId,
  onAction,
  onClose,
}: {
  rows: VoiceOpsCampaign[];
  lang: string;
  busyId: number | null;
  onAction: (a: PendingAction) => void;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  // Most recently paused first — the campaign someone stopped ten minutes ago
  // is far likelier to be the one being looked for than last Tuesday's.
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b.paused_at ?? '').localeCompare(a.paused_at ?? '')),
    [rows],
  );
  const head = 'px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-4';
  const numHead = cn(head, 'text-right');
  return (
    <WideModal title={t('ops_paused_title')} onClose={onClose}>
      <p className="text-[12.5px] text-text-4 mb-3 leading-snug">
        {t('ops_paused_intro', { days: PAUSED_WINDOW_DAYS })}
      </p>
      {sorted.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-text-4">
          {t('ops_paused_empty', { days: PAUSED_WINDOW_DAYS })}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="border-b border-line bg-surface-2">
                <th className={cn(head, 'text-left')}>{t('vops_col_campaign')}</th>
                <th className={cn(head, 'text-left')}>{t('ops_paused_col_since')}</th>
                <th className={cn(head, 'text-left', 'cursor-help')} title={t('ops_col_pacing_help')}>
                  {t('ops_col_pacing')}
                </th>
                <th className={numHead}>{t('vops_col_remaining')}</th>
                <th className={numHead}>{t('vops_col_answered')}</th>
                <th className={numHead}>{t('vops_col_transfers')}</th>
                <th className={numHead}>{t('vops_col_balance')}</th>
                <th className={numHead}>{t('ops_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <PausedRow
                  key={`${c.tenant_id}-${c.campaign_id}`}
                  c={c}
                  lang={lang}
                  busy={busyId === c.campaign_id}
                  onAction={onAction}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </WideModal>
  );
}

function PausedRow({
  c,
  lang,
  busy,
  onAction,
}: {
  c: VoiceOpsCampaign;
  lang: string;
  busy: boolean;
  onAction: (a: PendingAction) => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  const num = 'px-3 py-2.5 text-right tabular-nums';
  const trunkLabel = c.trunk || `#${c.trunk_id}`;
  return (
    <tr className="border-b border-line/60 hover:bg-surface-2/50">
      <td className="px-3 py-2.5 text-left">
        <div className="min-w-0 max-w-[280px]">
          <div className="font-medium text-text truncate">{c.campaign_name}</div>
          <div className="text-[11px] text-text-4 truncate">
            {c.tenant_name} · {trunkLabel}
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5 text-left text-text-3 whitespace-nowrap">
        {c.paused_at ? (
          <span title={t('ops_paused_since_title', { when: formatDateTime(c.paused_at, lang) })}>
            {formatRelativeTime(c.paused_at, lang)}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-3 py-2.5 text-left text-text-3 tabular-nums whitespace-nowrap">
        {t('ops_paused_pacing_value', { cpm: c.pacing_cpm, conc: c.max_concurrent })}
      </td>
      <td className={cn(num, 'text-text')}>{c.remaining.toLocaleString(lang)}</td>
      <td className={cn(num, 'text-text-3')}>{c.answered.toLocaleString(lang)}</td>
      <td className={cn(num, c.transfers > 0 ? 'text-success font-medium' : 'text-text-3')}>
        {c.transfers.toLocaleString(lang)}
      </td>
      <td className={cn(num, semText[balanceSem(c)])}>{formatCurrency(c.balance, lang)}</td>
      <td className="px-3 py-2.5 text-right">
        {/* Text button here, not an IconAction: the panel has room, and this
            is the one action a rarely-visited consult surface offers. */}
        <Button
          size="sm"
          variant="primary"
          disabled={busy}
          onClick={() => onAction({ kind: 'resume', c })}
          title={t('ops_action_activate_help')}
        >
          <Icon.play className="w-3.5 h-3.5" aria-hidden />
          {t('ops_action_activate')}
        </Button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Transfers-today drill-down
// ---------------------------------------------------------------------------

// TransfersTodayPanel — the list behind the "Transfers today" KPI tile, in the
// same shared WideModal the paused panel uses.
//
// It exists because the tile's number was ambiguous in the one way that
// matters: a transfer that never connected counted exactly like one that put a
// human on the phone. Since PR #539 call_records records both halves
// (bridge_seconds = did it connect, bridge_hangup_cause = why not), so every
// row here lands in one of three states:
//
//   - CONNECTED (success) — the bridge carried audio, with its duration.
//   - FAILED (danger) — the bridge was attempted and did not connect, with the
//     carrier's cause, or "unknown" when FreeSWITCH reported none.
//   - IN PROGRESS (info, neutral) — the call is still up. Both bridge columns
//     are written on hangup, so a live conversation carries neither yet; it
//     used to render as a failure until it ended, which is why the third
//     state exists.
//
// The split is `state`, NEVER the call's `status`: an A-leg that hangs up
// normally reports status='completed' either way, which is precisely the
// confusion this panel resolves. Read-only — no actions here.
//
// Above the table the same data is shown as charts (TransfersCharts): a donut
// for today's connected/failed split and a stacked bar per hour. The hourly
// one is the reason this section exists — a list sorted newest-first shows
// WHAT failed but never WHEN it started, which is the question every incident
// begins with.
function TransfersTodayPanel({ lang, onClose }: { lang: string; onClose: () => void }): JSX.Element {
  const { t } = useTranslation('default');
  // "Show more" grows the page rather than paging: an operator scanning
  // today's failures wants one continuous list, not page 2 of 5.
  const [limit, setLimit] = useState(TRANSFERS_TODAY_PAGE);
  const q = useAdminVoiceOpsTransfersToday({ enabled: true, limit });
  const page = q.data;
  const items = page?.items ?? [];
  const canShowMore = page != null && items.length < page.total && limit < TRANSFERS_TODAY_MAX;
  const head = 'px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-4';

  return (
    <WideModal title={t('ops_xfer_title')} onClose={onClose}>
      <p className="text-[12.5px] text-text-4 mb-3 leading-snug">{t('ops_xfer_intro')}</p>

      {page && (
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <SummaryPill sem="muted" label={t('ops_xfer_sum_total')} value={page.total.toLocaleString(lang)} />
          <SummaryPill sem="success" label={t('ops_xfer_sum_connected')} value={page.connected.toLocaleString(lang)} />
          <SummaryPill sem="danger" label={t('ops_xfer_sum_failed')} value={page.failed.toLocaleString(lang)} />
          <SummaryPill sem="info" label={t('ops_xfer_sum_in_progress')} value={page.in_progress.toLocaleString(lang)} />
        </div>
      )}

      {/* Same numbers as the pills, shaped: the split as a ring and the day
          hour by hour. Skipped on an empty day, where every series is 0. */}
      {page != null && page.total > 0 && <TransfersCharts page={page} lang={lang} />}

      {q.isError ? (
        <div className="py-10 text-center text-[13px] text-text-3">{t('ops_xfer_load_error')}</div>
      ) : q.isLoading ? (
        <div className="py-10 text-center text-[13px] text-text-4">{t('vops_loading')}</div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-[13px] text-text-4">{t('ops_xfer_empty')}</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="border-b border-line bg-surface-2">
                  <th className={cn(head, 'text-left')}>{t('ops_xfer_col_time')}</th>
                  <th className={cn(head, 'text-left')}>{t('vops_col_campaign')}</th>
                  <th className={cn(head, 'text-left')}>{t('ops_xfer_col_contact')}</th>
                  <th className={cn(head, 'text-left')}>{t('ops_xfer_col_destination')}</th>
                  <th className={cn(head, 'text-left')}>{t('ops_xfer_col_outcome')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((tr) => (
                  <TransferRow key={`${tr.campaign_id}-${tr.initiated_at}-${tr.to_e164}`} tr={tr} lang={lang} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-[11.5px] text-text-4">
              {t('ops_xfer_showing', { shown: items.length, total: page?.total ?? items.length })}
            </span>
            {canShowMore && (
              <Button
                size="sm"
                variant="secondary"
                disabled={q.isFetching}
                onClick={() => setLimit((n) => Math.min(n + TRANSFERS_TODAY_PAGE, TRANSFERS_TODAY_MAX))}
              >
                {t('ops_xfer_show_more')}
              </Button>
            )}
          </div>
          {page != null && page.total > TRANSFERS_TODAY_MAX && limit >= TRANSFERS_TODAY_MAX && (
            <p className="mt-2 text-[11.5px] text-text-4">{t('ops_xfer_capped', { max: TRANSFERS_TODAY_MAX })}</p>
          )}
        </>
      )}
    </WideModal>
  );
}

// TransfersCharts — the graphic half of the drill-down, above the table.
//
// Two questions an operator asks of today's transfers, neither of which a
// newest-first list answers at a glance:
//
//   1. "How much of today actually connected?" — the donut, one ring split
//      connected / failed / in progress with the connect rate in the middle.
//      The rate is connected over the DECIDED attempts (connected + failed):
//      a transfer still on the phone has not resolved either way, so counting
//      it in the denominator would drag the rate down for no reason.
//   2. "WHEN did it stop connecting?" — the hourly bars. This is the one that
//      matters. During the 2026-09-11 session-limit incident the flat list
//      showed hundreds of failures with no shape; the same data per hour makes
//      a change at 18:00 a step in the chart instead of a scroll.
//
// Both read the server-side `hourly` aggregate / whole-set counters, never a
// rollup of the loaded rows, so "Show more" never moves a bar. Colors are the
// page's semantic tokens — the same green/red/blue as the row badges below.
function TransfersCharts({
  page,
  lang,
}: {
  page: VoiceOpsTransfersPage;
  lang: string;
}): JSX.Element {
  const { t } = useTranslation('default');
  const decided = page.connected + page.failed;
  const rate = decided > 0 ? page.connected / decided : 0;
  const donut = [
    { key: 'connected', label: t('ops_xfer_sum_connected'), value: page.connected, fill: 'var(--success)' },
    { key: 'failed', label: t('ops_xfer_sum_failed'), value: page.failed, fill: 'var(--danger)' },
    { key: 'in_progress', label: t('ops_xfer_sum_in_progress'), value: page.in_progress, fill: 'var(--info)' },
  ].filter((s) => s.value > 0);

  // Two-digit UTC hour labels ("00" … "23"). Built from the bucket's own hour
  // number rather than from hour_start's local time: the buckets are UTC (the
  // day boundary is), so localizing the axis would mislabel every bar.
  const bars = page.hourly.map((h) => ({
    hour: h.hour,
    label: String(h.hour).padStart(2, '0'),
    connected: h.connected,
    failed: h.failed,
    in_progress: h.in_progress,
    total: h.total,
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 mb-4">
      <div className="lg:col-span-2 rounded-lg border border-line bg-surface-2 p-3">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-text-4 mb-1">
          {t('ops_xfer_chart_outcome')}
        </div>
        <div className="relative h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={donut}
                dataKey="value"
                nameKey="label"
                innerRadius={48}
                outerRadius={72}
                paddingAngle={2}
                stroke="var(--surface)"
                strokeWidth={2}
              >
                {donut.map((s) => (
                  <Cell key={s.key} fill={s.fill} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
                formatter={(value: number, name: string) => [value.toLocaleString(lang), name]}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            <div className="text-center">
              <div className="num text-[20px] font-semibold text-text tabular-nums">
                {(rate * 100).toFixed(1)}%
              </div>
              <div className="text-[10.5px] text-text-4 uppercase tracking-[0.06em] mt-0.5">
                {t('ops_xfer_connect_rate')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-3 rounded-lg border border-line bg-surface-2 p-3">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text-4">
            {t('ops_xfer_chart_hourly')}
          </span>
          <span className="flex items-center gap-3 text-[11.5px] text-text-3">
            <ChartLegendDot sem="success" label={t('ops_xfer_sum_connected')} />
            <ChartLegendDot sem="danger" label={t('ops_xfer_sum_failed')} />
            <ChartLegendDot sem="info" label={t('ops_xfer_sum_in_progress')} />
          </span>
        </div>
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: 'var(--text-3)', fontSize: 11 }}
                axisLine={{ stroke: 'var(--line)' }}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                tick={{ fill: 'var(--text-3)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                width={34}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: 'var(--surface-2)' }}
                contentStyle={{
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: 6,
                  fontSize: 12,
                }}
                formatter={(value: number, name: string) => [value.toLocaleString(lang), name]}
                labelFormatter={(_, payload) => {
                  const h = payload?.[0]?.payload?.hour as number | undefined;
                  if (h == null) return '';
                  return t('ops_xfer_chart_hour_range', {
                    from: `${String(h).padStart(2, '0')}:00`,
                    to: `${String((h + 1) % 24).padStart(2, '0')}:00`,
                  });
                }}
              />
              <Bar dataKey="connected" name={t('ops_xfer_sum_connected')} stackId="x" fill="var(--success)" />
              <Bar dataKey="failed" name={t('ops_xfer_sum_failed')} stackId="x" fill="var(--danger)" />
              <Bar dataKey="in_progress" name={t('ops_xfer_sum_in_progress')} stackId="x" fill="var(--info)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ChartLegendDot — a swatch + label for the hourly chart, using the same
// semantic dot the table's row badges use so the two read as one legend.
function ChartLegendDot({ sem, label }: { sem: Sem; label: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('inline-block w-2 h-2 rounded-sm', semDot[sem])} aria-hidden />
      {label}
    </span>
  );
}

// SummaryPill — one of the four whole-set counters above the table. Uses the
// page's semantic tag tokens so connected/failed/in-progress read the same
// green/red/blue as every other outcome on this screen.
function SummaryPill({ sem, label, value }: { sem: Sem; label: string; value: string }): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12.5px] font-semibold',
        semTag[sem],
      )}
    >
      <span className={cn('inline-block w-2 h-2 rounded-full', semDot[sem])} aria-hidden />
      {label}
      <span className="tabular-nums">{value}</span>
    </span>
  );
}

// Badge color per transfer state. In progress is deliberately `info`, not
// `danger`: an open call is not a failure, and it was rendering as one.
const transferStateSem: Record<VoiceOpsTransferState, Sem> = {
  connected: 'success',
  failed: 'danger',
  in_progress: 'info',
};

const transferStateLabelKey: Record<VoiceOpsTransferState, string> = {
  connected: 'ops_xfer_connected',
  failed: 'ops_xfer_failed',
  in_progress: 'ops_xfer_in_progress',
};

function TransferRow({ tr, lang }: { tr: VoiceOpsTransfer; lang: string }): JSX.Element {
  const { t } = useTranslation('default');
  const sem = transferStateSem[tr.state];
  // Detail next to the badge: duration once connected, the carrier's cause
  // once failed, and nothing while in progress — there is no outcome to
  // explain yet, and "no reason reported" would read as a failure.
  const detail =
    tr.state === 'connected'
      ? t('ops_xfer_duration', { seconds: tr.bridge_seconds.toLocaleString(lang) })
      : tr.state === 'failed'
        ? tr.cause || t('ops_xfer_cause_unknown')
        : '';
  return (
    <tr className="border-b border-line/60 hover:bg-surface-2/50">
      <td className="px-3 py-2.5 text-left text-text-3 whitespace-nowrap">
        {formatDateTime(tr.initiated_at, lang)}
      </td>
      <td className="px-3 py-2.5 text-left">
        <div className="min-w-0 max-w-[240px]">
          <div className="font-medium text-text truncate">{tr.campaign_name}</div>
          <div className="text-[11px] text-text-4 truncate">{tr.tenant_name}</div>
        </div>
      </td>
      <td className="px-3 py-2.5 text-left text-text tabular-nums whitespace-nowrap">{tr.to_e164 || '—'}</td>
      <td className="px-3 py-2.5 text-left text-text-3 tabular-nums whitespace-nowrap">{tr.bridged_to || '—'}</td>
      <td className="px-3 py-2.5 text-left">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold whitespace-nowrap',
            semTag[sem],
          )}
        >
          <span
            className={cn('inline-block w-1.5 h-1.5 rounded-full', semDot[sem], tr.state === 'in_progress' && 'animate-pulse')}
            aria-hidden
          />
          {t(transferStateLabelKey[tr.state])}
        </span>
        {detail && <span className="ml-2 text-[12px] text-text-3">{detail}</span>}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Section 1 — health tiles
// ---------------------------------------------------------------------------

function HealthTile({ c }: { c: HealthComponent }): JSX.Element {
  const { t } = useTranslation('default');
  const sem = statusSem[c.status];
  // Localized component label, falling back to the API's English label when no
  // i18n key exists for the component key.
  const key = `health_comp_${c.key}`;
  const translated = t(key);
  const label = translated === key ? c.label : translated;
  const showTag = c.status === 'warn' || c.status === 'crit';
  return (
    <Card padding="none" className="relative overflow-hidden p-3 pl-3.5">
      <span className={cn('absolute left-0 top-0 bottom-0 w-[3px]', semBar[sem])} aria-hidden />
      {showTag && (
        <span
          className={cn(
            'absolute top-2.5 right-2.5 rounded px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide',
            semTag[sem],
          )}
        >
          {t(`health_status_${c.status}`)}
        </span>
      )}
      <div className="flex items-center gap-1.5 mb-2">
        <span className={cn('inline-block w-2 h-2 rounded-full flex-none', semDot[sem])} aria-hidden />
        <span className="text-[11.5px] font-semibold text-text-2 truncate">{label}</span>
      </div>
      {/* Primary metric: big + NEUTRAL (text-1). Status color stays on the bar,
          dot and tag only — never on this value. */}
      <div className="text-[20px] font-semibold tabular-nums leading-none text-text">{c.metric || '—'}</div>
      {/* One-line, muted, ellipsis-clamped for uniform tile heights; full text in
          the title tooltip so nothing is lost. */}
      <div className="text-[11.5px] text-text-4 mt-1.5 leading-snug truncate" title={c.detail}>
        {c.detail}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — KPI band
// ---------------------------------------------------------------------------

function KpiBand({ kpi, lang, onOpenTransfers }: { kpi: KpiAgg; lang: string; onOpenTransfers: () => void }): JSX.Element {
  const { t } = useTranslation('default');
  const items: KpiItem[] = [
    { label: t('ops_kpi_running'), value: String(kpi.running), sub: t('ops_kpi_running_sub') },
    { label: t('ops_kpi_active'), value: kpi.active.toLocaleString(lang) },
    { label: t('ops_kpi_cps'), value: kpi.cps.toFixed(1) },
    { label: t('ops_kpi_answered'), value: kpi.answered.toLocaleString(lang) },
    {
      label: t('ops_kpi_transfers'),
      value: kpi.transfers.toLocaleString(lang),
      sub: t('ops_kpi_transfers_sub'),
      // The only drill-down on the band: a transfer count says nothing about
      // whether those transfers reached anyone, and since #539 the answer is
      // in call_records. Always clickable, including at 0 — an operator who
      // expected transfers and sees none still wants the (empty) list rather
      // than a dead tile.
      onClick: onOpenTransfers,
      hint: t('ops_kpi_transfers_hint'),
    },
    { label: t('ops_kpi_machine'), value: kpi.hasAnswered ? pct0(kpi.machinePct) : '—', sub: t('ops_kpi_machine_sub') },
  ];
  return <KpiGrid items={items} className="lg:grid-cols-6" />;
}

// ---------------------------------------------------------------------------
// Section 3 — running campaigns table
// ---------------------------------------------------------------------------

function OpsTable({
  rows,
  lang,
  busyId,
  onAction,
}: {
  rows: VoiceOpsCampaign[];
  lang: string;
  /** campaign_id whose mutation is in flight, or null. Disables its controls. */
  busyId: number | null;
  onAction: (a: PendingAction) => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  const headCls = 'px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-text-4';
  const numHead = cn(headCls, 'text-right');
  return (
    <table className="w-full text-[13px] border-collapse min-w-[1480px]">
      <thead>
        <tr className="border-b border-line bg-surface-2">
          <th className={cn(headCls, 'text-left')}>{t('vops_col_campaign')}</th>
          <th className={numHead}>{t('vops_col_cps')}</th>
          <th className={cn(numHead, 'cursor-help')} title={t('ops_col_503_help')}>
            {t('ops_col_503')}
          </th>
          <th className={cn(headCls, 'text-left', 'cursor-help')} title={t('ops_col_pacing_help')}>
            {t('ops_col_pacing')}
          </th>
          <th className={cn(numHead, 'cursor-help')} title={t('vops_col_active_help')}>
            {t('vops_col_active')}
          </th>
          <th className={numHead}>{t('vops_col_answered')}</th>
          <th className={numHead}>{t('vops_col_machine')}</th>
          <th className={numHead}>{t('vops_col_human')}</th>
          <th className={numHead}>{t('vops_col_transfers')}</th>
          <th className={numHead}>{t('vops_col_optout')}</th>
          <th className={numHead}>{t('vops_col_603')}</th>
          <th className={numHead}>{t('vops_col_balance')}</th>
          <th className={cn(headCls, 'text-right')}>{t('ops_col_actions')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <Row
            key={`${c.tenant_id}-${c.campaign_id}`}
            c={c}
            lang={lang}
            busy={busyId === c.campaign_id}
            onAction={onAction}
          />
        ))}
      </tbody>
    </table>
  );
}

function Row({
  c,
  lang,
  busy,
  onAction,
}: {
  c: VoiceOpsCampaign;
  lang: string;
  busy: boolean;
  onAction: (a: PendingAction) => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  const num = 'px-3 py-2.5 text-right tabular-nums';
  const pileup = isPileup(c);
  const trunkLabel = c.trunk || `#${c.trunk_id}`;
  const sem = rowSem(c);
  return (
    <tr className="border-b border-line/60 hover:bg-surface-2/50">
      <td className="px-3 py-2.5 text-left">
        <div className="flex items-center gap-2 max-w-[320px]">
          <span
            className={cn('inline-block w-2.5 h-2.5 rounded-full flex-none', semDot[sem])}
            title={t(`ops_row_sem_${sem === 'muted' ? 'success' : sem}`)}
            aria-hidden
          />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-text truncate">{c.campaign_name}</span>
              <StatusBadge c={c} />
              {c.concurrency_capped && (
                <span
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold text-warn bg-warn/10 shrink-0"
                  title={c.concurrency_warning}
                >
                  <Icon.warn className="w-3 h-3" aria-hidden />
                  {t('vops_concurrency_capped_badge')}
                </span>
              )}
            </div>
            <div className="text-[11px] text-text-4 truncate">
              {c.tenant_name} · {trunkLabel}
            </div>
          </div>
        </div>
      </td>
      <td className={num}>
        <CpsCell c={c} />
      </td>
      <td className={num}>
        <Fail503Cell c={c} />
      </td>
      <td className="px-3 py-2.5 text-left">
        <PacingCell c={c} busy={busy} onAction={onAction} />
      </td>
      <td className={num}>
        <span className="inline-flex items-center gap-1.5 justify-end">
          <span className="text-text" title={t('vops_col_active_help')}>
            ~{c.active_approx.toLocaleString(lang)}
          </span>
          {pileup && (
            <span
              className="inline-flex items-center rounded px-1 py-0.5 text-[10px] font-semibold text-warn bg-warn/10"
              title={t('vops_incall_title')}
            >
              +{c.incall_leads}
            </span>
          )}
        </span>
      </td>
      <td className={cn(num, 'text-text')}>{c.answered.toLocaleString(lang)}</td>
      <td className={cn(num, 'text-text-3')}>{c.answered > 0 ? pct1(machinePct(c)) : '—'}</td>
      <td className={cn(num, 'text-text')}>{c.human.toLocaleString(lang)}</td>
      <td className={cn(num, c.transfers > 0 ? 'text-success font-medium' : 'text-text-3')}>
        {c.transfers.toLocaleString(lang)}
      </td>
      <td className={cn(num, 'text-text-3')}>{c.optouts.toLocaleString(lang)}</td>
      <td className={num}>
        <span
          className={semText[c603Sem(c)]}
          title={c603Pct(c) > C603_WARN ? t('vops_603_high_title') : undefined}
        >
          {c.total > 0 ? pct1(c603Pct(c)) : '—'}
        </span>
      </td>
      <td className={num}>
        <BalanceCell c={c} lang={lang} />
      </td>
      <td className="px-3 py-2.5 text-right">
        <ActionsCell c={c} busy={busy} onAction={onAction} />
      </td>
    </tr>
  );
}

// StatusBadge marks a row that is NOT dialing. A campaign parked by a restart
// toggle gets its own label so an operator doesn't read the ~35s pause as
// "someone stopped this campaign" and undo it mid-restart.
function StatusBadge({ c }: { c: VoiceOpsCampaign }): JSX.Element | null {
  const { t } = useTranslation('default');
  if (c.restarting) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold text-warn bg-warn/10 shrink-0"
        title={t('ops_restarting_title', { seconds: RESTART_SECONDS })}
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-warn animate-pulse" aria-hidden />
        {t('ops_restarting_badge')}
      </span>
    );
  }
  if (c.status !== 'running') {
    return (
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold text-text-3 bg-text-4/10 shrink-0">
        {t('ops_status_paused_badge')}
      </span>
    );
  }
  return null;
}

// Fail503Cell is the pacing feedback signal: the share of the last 5 minutes'
// dials the carrier refused with SIP 503 / NORMAL_TEMPORARY_FAILURE. It sits
// immediately left of the pacing control on purpose — lower the pacing, watch
// this fall. Shows a dash under RECENT_FAIL_MIN_DIALS dials, because a ratio
// over a handful of calls is noise an operator must not tune against.
function Fail503Cell({ c }: { c: VoiceOpsCampaign }): JSX.Element {
  const { t } = useTranslation('default');
  if (!hasFailSignal(c)) {
    return (
      <span className="text-text-4" title={t('ops_503_low_sample', { dials: c.recent_dials, min: RECENT_FAIL_MIN_DIALS })}>
        —
      </span>
    );
  }
  const sem = fail503Sem(c);
  return (
    <span
      className={cn('inline-flex flex-col items-end', semText[sem])}
      title={t('ops_503_title', {
        c503: c.recent_503,
        dials: c.recent_dials,
        failPct: pct1(c.recent_fail_pct),
      })}
    >
      <span className={cn('font-semibold', sem === 'success' && 'text-text')}>{pct1(c.recent_503_pct)}</span>
      <span className="text-[10px] font-normal text-text-4">{t('ops_503_sub', { dials: c.recent_dials })}</span>
    </span>
  );
}

// PacingCell — the two LIVE knobs. Editing them does NOT restart anything: the
// poller's runner re-reads both columns from the DB on a timer, so a change
// reaches an already-dialing campaign within seconds. The Apply button only
// lights up when a value actually differs from what the server reports, and
// the confirmation spells out the old → new pair.
function PacingCell({
  c,
  busy,
  onAction,
}: {
  c: VoiceOpsCampaign;
  busy: boolean;
  onAction: (a: PendingAction) => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  const [cpm, setCpm] = useState(String(c.pacing_cpm));
  const [conc, setConc] = useState(String(c.max_concurrent));

  // Re-seed from the server whenever IT changes the values (another operator,
  // or this operator's own apply landing). Depending on the two numbers means
  // the 10s auto-refresh does not clobber a half-typed edit.
  useEffect(() => {
    setCpm(String(c.pacing_cpm));
  }, [c.pacing_cpm]);
  useEffect(() => {
    setConc(String(c.max_concurrent));
  }, [c.max_concurrent]);

  // Collapsed by default: the cell shows "cpm / conc" as plain numbers with a
  // pencil, and the two inputs + Apply live in a small popover. The inline
  // form was the widest thing in the widest table (two labelled inputs plus a
  // button per row); operators read these values far more often than they
  // change them. Escape / outside click / Cancel discard the edit.
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const cancel = () => {
    setCpm(String(c.pacing_cpm));
    setConc(String(c.max_concurrent));
    setOpen(false);
  };

  const cpmNum = Number(cpm);
  const concNum = Number(conc);
  // Bounds mirror the API (MinPacingCPM..MaxPacingCPM, MinConcurrency..
  // MaxConcurrency in admin_campaign_ops.go). Client-side they only gate the
  // button; the server re-validates and is the authority.
  const cpmValid = Number.isInteger(cpmNum) && cpmNum >= 1 && cpmNum <= 10000;
  const concValid = Number.isInteger(concNum) && concNum >= 1 && concNum <= 10000;
  const dirty = cpmNum !== c.pacing_cpm || concNum !== c.max_concurrent;
  const canApply = dirty && cpmValid && concValid && !busy;

  const inputCls = 'h-7 w-[68px] px-1.5 text-[12px] text-right tabular-nums';

  return (
    <div ref={popRef} className="relative inline-block">
      <button
        type="button"
        disabled={busy}
        onClick={() => setOpen((v) => !v)}
        title={t('ops_pacing_edit_hint')}
        aria-label={t('ops_pacing_edit_aria', { name: c.campaign_name })}
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12.5px] tabular-nums text-text',
          'hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/40',
          busy && 'opacity-60 cursor-default',
        )}
      >
        <span>{c.pacing_cpm.toLocaleString()}</span>
        <span className="text-text-4">/</span>
        <span>{c.max_concurrent.toLocaleString()}</span>
        <Icon.pencil width="11" height="11" className="text-text-4 ml-0.5" />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('ops_col_pacing')}
          className="absolute left-0 top-full mt-1 z-20 rounded-md border border-line bg-surface shadow-lg p-2"
        >
          <div className="flex items-end gap-1.5">
            <label className="inline-flex flex-col gap-0.5">
              <span className="text-[9.5px] uppercase tracking-wide text-text-4">{t('ops_pacing_cpm_label')}</span>
              <Input
                type="number"
                min={1}
                max={10000}
                value={cpm}
                error={!cpmValid}
                disabled={busy}
                autoFocus
                onChange={(e) => setCpm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canApply) {
                    onAction({ kind: 'pacing', c, pacingCpm: cpmNum, maxConcurrent: concNum });
                    setOpen(false);
                  }
                }}
                className={inputCls}
                aria-label={t('ops_pacing_cpm_aria', { name: c.campaign_name })}
              />
            </label>
            <label className="inline-flex flex-col gap-0.5">
              <span className="text-[9.5px] uppercase tracking-wide text-text-4">{t('ops_pacing_conc_label')}</span>
              <Input
                type="number"
                min={1}
                max={1000}
                value={conc}
                error={!concValid}
                disabled={busy}
                onChange={(e) => setConc(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canApply) {
                    onAction({ kind: 'pacing', c, pacingCpm: cpmNum, maxConcurrent: concNum });
                    setOpen(false);
                  }
                }}
                className={inputCls}
                aria-label={t('ops_pacing_conc_aria', { name: c.campaign_name })}
              />
            </label>
            <Button
              size="sm"
              disabled={!canApply}
              title={t('ops_action_apply')}
              aria-label={t('ops_action_apply')}
              onClick={() => {
                onAction({ kind: 'pacing', c, pacingCpm: cpmNum, maxConcurrent: concNum });
                setOpen(false);
              }}
            >
              <Icon.check width="13" height="13" />
            </Button>
            <Button size="sm" variant="secondary" onClick={cancel} aria-label={t('cancel')} title={t('cancel')}>
              <Icon.xMark width="12" height="12" />
            </Button>
          </div>
          <div className="mt-1.5 text-[11px] text-text-4">{t('ops_pacing_live_note')}</div>
        </div>
      )}
    </div>
  );
}

// ActionsCell — Pause / Activate / Restart runner, ICON-ONLY (IconAction), so
// the widest table in the app spends no horizontal space on button labels. The
// action name is still the native tooltip AND the aria-label, the table footer
// carries a legend, and the ConfirmModal spells out the consequence before
// anything happens — nothing but the visual affordance changed here.
//
// Which buttons are offered follows the campaign's status, mirroring the
// transitions the API accepts, so an operator is never shown a button that can
// only return a 409. Everything is disabled while a mutation for this row is
// in flight. In the live table only running (or mid-restart) rows reach here;
// the Activate branch is the safety net for any other state a future filter
// might let through, and is the action the paused panel offers.
function ActionsCell({
  c,
  busy,
  onAction,
}: {
  c: VoiceOpsCampaign;
  busy: boolean;
  onAction: (a: PendingAction) => void;
}): JSX.Element {
  const { t } = useTranslation('default');
  const running = c.status === 'running';

  // A restart already in flight: no buttons, just the countdown state. Acting
  // now would either be rejected (409) or race the pending resume.
  if (c.restarting) {
    return (
      <span className="text-[11.5px] text-warn" title={t('ops_restarting_title', { seconds: RESTART_SECONDS })}>
        {t('ops_restarting_pending')}
      </span>
    );
  }

  return (
    <div className="inline-flex items-center gap-0.5 justify-end">
      {running ? (
        <>
          {/* Same icon + tone vocabulary as voice/dids.tsx: play = start,
              stop (amber) = stop. */}
          <IconAction
            icon={Icon.stop}
            label={t('ops_action_pause')}
            tone="warn"
            disabled={busy}
            onClick={() => onAction({ kind: 'pause', c })}
          />
          <IconAction
            icon={Icon.refresh}
            label={t('ops_action_restart')}
            disabled={busy}
            onClick={() => onAction({ kind: 'restart', c })}
          />
        </>
      ) : (
        <IconAction
          icon={Icon.play}
          label={t('ops_action_activate')}
          tone="primary"
          disabled={busy}
          onClick={() => onAction({ kind: 'resume', c })}
        />
      )}
    </div>
  );
}

// CpsCell shows the actual CPS prominently with a muted "/target"; amber when
// the campaign is concurrency-capped or dialing far under target. When capped,
// the concurrency warning is surfaced as the tooltip.
function CpsCell({ c }: { c: VoiceOpsCampaign }): JSX.Element {
  const { t } = useTranslation('default');
  const title = c.concurrency_capped
    ? c.concurrency_warning
    : c.cps_current === 0
      ? t('vops_cps_stalled_title')
      : undefined;
  return <CpsValue current={c.cps_current} target={c.cps_target} amber={cpsAmber(c)} title={title} />;
}

// BalanceCell shows the RAW balance (the real money/debt, can be negative) so a
// "Balance" header isn't misread. The semaphore still uses `available` (balance
// + credit) — a campaign only stalls when that hits zero. When the tenant has a
// credit line, a small "credit $X" line + a breakdown tooltip explain why a
// negative balance is still dialing.
function BalanceCell({ c, lang }: { c: VoiceOpsCampaign; lang: string }): JSX.Element {
  const { t } = useTranslation('default');
  const hasCredit = c.credit_limit > 0;
  const title = hasCredit
    ? t('vops_balance_breakdown_title', {
        balance: formatCurrency(c.balance, lang),
        credit: formatCurrency(c.credit_limit, lang),
        available: formatCurrency(c.available, lang),
      })
    : c.available <= 0
      ? t('vops_balance_empty_title')
      : c.available < BALANCE_LOW
        ? t('vops_balance_low_title')
        : undefined;
  return (
    <span className={cn('inline-flex flex-col items-end', semText[balanceSem(c)])} title={title}>
      <span>{formatCurrency(c.balance, lang)}</span>
      {hasCredit && (
        <span className="text-[10px] font-normal text-text-4">
          {t('vops_balance_credit_line', { credit: formatCurrency(c.credit_limit, lang) })}
        </span>
      )}
    </span>
  );
}

function TableFooter(): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <div className="flex flex-wrap items-center gap-4 px-4 py-2.5 border-t border-line text-[11.5px] text-text-4">
      <Legend sem="success" label={t('ops_legend_healthy')} />
      <Legend sem="warn" label={t('ops_legend_attention')} />
      <Legend sem="danger" label={t('ops_legend_critical')} />
      {/* Icon legend — the actions column is icon-only, so the names (and
          their full explanations, as tooltips) live here. Only the two actions
          a RUNNING campaign offers: activate belongs to the paused panel,
          which spells it out on a text button. */}
      <span className="inline-flex items-center gap-3">
        <span className="uppercase tracking-wide text-[10.5px]">{t('ops_col_actions')}</span>
        <ActionLegend icon={Icon.stop} label={t('ops_action_pause')} help={t('ops_action_pause_help')} />
        <ActionLegend
          icon={Icon.refresh}
          label={t('ops_action_restart')}
          help={t('ops_action_restart_help', { seconds: RESTART_SECONDS })}
        />
      </span>
      <span className="flex-1" />
      <span className="min-w-0">{t('ops_table_note')}</span>
      <span className="min-w-0 text-success">{t('ops_pacing_live_note')}</span>
    </div>
  );
}
